import React from 'react';
import { useHistory } from 'react-router';
import { ResetPasswordForm } from 'shared/components';
import {
  CanonicalLink as MetaCanonicalLink,
  Title as MetaTitle,
} from 'shared/components/Meta';
import { defaultLocalStorageAdapter } from 'shared/graphql';
import { useResetPasswordMutation } from 'shared/graphql/generated';
import { useRefreshSession } from 'shared/graphql/hooks';
import { useLocation, useTranslate } from 'shared/hooks';
import { TranslationNamespace } from 'shared/hooks/useTranslate';
import { TwoColumn } from 'shared/layouts';
import { Route } from 'shared/route';
import { identify } from 'shared/telemetry';
import { baseUrlToRedirect } from 'shared/utils/url';

const ResetPassword = () => {
  const [resetPassword, { error, loading }] = useResetPasswordMutation();
  const t = useTranslate(TranslationNamespace.LOGIN);
  const history = useHistory();
  const { href } = useLocation();
  const refreshSession = useRefreshSession();

  const onSubmit = async (formData: { password: string }, token: string) => {
    try {
      const { data } = await resetPassword({
        variables: { ...formData, token },
      });

      if (data) {
        const {
          user: { id, email, intercomSha },
          jwt: { accessToken, refreshToken },
        } = data.resetPassword;

        try {
          await identify({
            email,
            userId: id,
            intercomSha,
          });
        } finally {
          void defaultLocalStorageAdapter.setAccessToken(accessToken);
          void defaultLocalStorageAdapter.setRefreshToken(refreshToken);
          await refreshSession();
          const url = baseUrlToRedirect(href, Route.PROPERTIES.path);

          history.push({
            pathname: url.pathname,
          });
        }
      }
    } catch {
      // TODO: expected authentication errors should probably be
      // handled in an Apollo error link at the client level
    }
  };

  return (
    <>
      <MetaCanonicalLink pathname={Route.LOGIN.path} />
      <MetaTitle title={t('resetPassword.recoverYourPassword')} />
      <TwoColumn.Header
        heading={t('resetPassword.recoverYourPassword')}
        subheading={t('resetPassword.createYourPassword')}
      />
      <TwoColumn.Main>
        <ResetPasswordForm
          error={error}
          loading={loading}
          onSubmit={onSubmit}
          submitText={t('resetPassword.resetPasswordLogIn')}
        />
      </TwoColumn.Main>
    </>
  );
};

export { ResetPassword };
